/**
 * Search input component for filtering
 * Optimised with debounce and minimum character requirements
 * Shared component for @uicwebmaster/events, @uicwebmaster/articles, etc.
 */

'use client';

import { Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface FilterSearchProps {
  query: string;
  onQueryChange: (query: string) => void;
  placeholder?: string;
  /** Minimum number of characters before triggering search (default: 3) */
  minChars?: number;
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
}

export function FilterSearch({
  query,
  onQueryChange,
  placeholder = 'Search...',
  minChars = 3,
  debounceMs = 300,
}: FilterSearchProps) {
  // Local state for immediate input feedback
  const [localValue, setLocalValue] = useState(query);
  const [isDebouncing, setIsDebouncing] = useState(false);

  // Use ref to avoid re-triggering effect when onQueryChange changes
  const onQueryChangeRef = useRef(onQueryChange);

  useEffect(() => {
    onQueryChangeRef.current = onQueryChange;
  }, [onQueryChange]);

  // Sync with external query changes
  useEffect(() => {
    setLocalValue(query);
  }, [query]);

  // Debounced search effect
  useEffect(() => {
    console.log('[FilterSearch] useEffect triggered', { localValue, minChars });

    // If input is cleared, update immediately
    if (localValue === '') {
      console.log('[FilterSearch] Clearing search');
      onQueryChangeRef.current('');
      setIsDebouncing(false);
      return;
    }

    // If below minimum characters, don't trigger search
    if (localValue.length < minChars) {
      console.log('[FilterSearch] Below min chars');
      setIsDebouncing(false);
      return;
    }

    // Set debouncing state
    console.log('[FilterSearch] Starting debounce');
    setIsDebouncing(true);

    // Debounce the search
    const timeoutId = setTimeout(() => {
      console.log('[FilterSearch] Debounce complete, calling onQueryChange');
      onQueryChangeRef.current(localValue);
      setIsDebouncing(false);
    }, debounceMs);

    return () => {
      console.log('[FilterSearch] Cleanup timeout');
      clearTimeout(timeoutId);
    };
  }, [localValue, debounceMs, minChars]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(event.target.value);
  }, []);

  const handleClear = useCallback(() => {
    console.log('[FilterSearch] Clear button clicked');
    setLocalValue('');
    onQueryChangeRef.current('');
    setIsDebouncing(false);
  }, []);

  const showMinCharsHint = localValue.length > 0 && localValue.length < minChars;
  const charsRemaining = minChars - localValue.length;

  return (
    <div className="w-full space-y-1">
      <label htmlFor="filter-search" className="sr-only">
        Search
      </label>
      <div className="relative">
        {/* Search icon */}
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

        {/* Input field */}
        <input
          id="filter-search"
          type="text"
          value={localValue}
          placeholder={placeholder}
          onChange={handleChange}
          autoComplete="off"
          className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-9 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
        />

        {/* Clear button */}
        {localValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Helper text */}
      {showMinCharsHint && (
        <p className="text-xs text-muted-foreground px-1">
          Type {charsRemaining} more character{charsRemaining !== 1 ? 's' : ''} to search
        </p>
      )}

      {isDebouncing && <p className="text-xs text-muted-foreground px-1">Searching...</p>}
    </div>
  );
}

